import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { linkUrl } from '../../utils/taskLinks';
import { userName } from '../../utils/taskUsers';
import { PROJECT_MAX_DEPTH } from '../../utils/taskTree';
import Popover from '../tasks/Popover';
import TaskIcon from '../tasks/TaskIcon';
import { ItemMenu, ItemMenuPanel } from '../tasks/ItemRow';
import { BubbleStack, UserPicker } from '../tasks/UserBubble';
import { kindIcon } from './planChips';

/**
 * The right-hand end of a project topic on the map and the Gantt chart: its
 * milestone / event mark, note, people and ⋯ menu — the same pickers and menu
 * as a list line (UserPicker, ItemMenu).
 */
const ItemActions: React.FC<{
  item: TaskItem;
  readOnly: boolean;
  hasChildren: boolean;
  onAddChild: () => void;
  maxPeople?: number;
  /** Offer "Add sub-item" (default: unless it's as deep as a project goes). */
  canAddChild?: boolean;
}> = ({ item, readOnly, hasChildren, onAddChild, maxPeople = 2, canAddChild }) => {
  const { users, me, updateItem } = useTasks();
  const navigate = useNavigate();
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState<ItemMenuPanel>('main');
  const [assignDraft, setAssignDraft] = useState<string[] | null>(null);

  const closeAssign = useCallback(() => {
    if (assignDraft) {
      const same = assignDraft.length === item.assignees.length && assignDraft.every((a) => item.assignees.includes(a));
      if (!same) updateItem(item.id, { assignees: assignDraft });
    }
    setAssignDraft(null);
  }, [assignDraft, item, updateItem]);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setPanel('main');
  }, []);
  const open = (p: ItemMenuPanel) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setPanel(p);
    setMenuOpen(true);
  };
  const names = item.assignees.map((a) => userName(users, a)).join(', ');
  const href = linkUrl(item.link_type, item.link_ref);

  return (
    <div className="tp-actions">
      {kindIcon(item)}
      {item.note && (
        <button type="button" className="tk-src tk-src-note" title={item.note} onClick={open('note')} aria-label="Show note">
          <TaskIcon name="note" size={13} />
        </button>
      )}
      <button
        ref={bubbleRef}
        type="button"
        className={`ti-assignee${item.assignees.length ? '' : ' is-empty'}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!readOnly) setAssignDraft([...item.assignees]);
        }}
        disabled={readOnly}
        aria-label={item.assignees.length ? `Assigned to ${names}` : 'Assign'}
        title={item.assignees.length ? names : 'Assign someone'}
      >
        {item.assignees.length ? (
          <BubbleStack users={users} emails={item.assignees} max={maxPeople} />
        ) : (
          <span className="tk-bubble tk-bubble-sm tk-bubble-empty"><TaskIcon name="user" size={11} /></span>
        )}
      </button>
      {!readOnly && (
        <button ref={menuBtnRef} type="button" className="tk-icon-btn ti-more tp-actions-more" onClick={open('main')} aria-label="Options">
          <TaskIcon name="more" />
        </button>
      )}

      {assignDraft && (
        <Popover anchor={bubbleRef.current} onClose={closeAssign} label="Assign">
          <div className="tk-menu-title">Assign to</div>
          <UserPicker users={users} me={me} value={assignDraft} onChange={setAssignDraft} />
        </Popover>
      )}
      {menuOpen && (
        <Popover anchor={menuBtnRef.current || bubbleRef.current} onClose={closeMenu} label="Options" width={panel === 'deps' ? 340 : 300}>
          <ItemMenu
            item={item}
            panel={panel}
            setPanel={setPanel}
            hasChildren={hasChildren}
            readOnly={readOnly}
            canAddChild={canAddChild ?? item.depth < PROJECT_MAX_DEPTH}
            onClose={closeMenu}
            onAddChild={onAddChild}
            onOpenLink={href ? () => navigate(href) : undefined}
          />
        </Popover>
      )}
    </div>
  );
};

export default ItemActions;
